import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle, SmallTitleWithSubSpan } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
    CreateCellClickHandler, CustomCellClickAction, createCellCssClassRules,
    getSwitchButtonColumnOptions, GridCellConfig, postSortHandler,
} from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { HRM3050_R01HeaderDto } from "./Dto_HRM3050_R01";
import { useHRM3050_R01Store } from "./Store_HRM3050_R01";
import { formatHRM3050_R01Time, headerNames_HRM3050_R01 } from "./Util_HRM3050_R01";
import { useHRM3050_R01CellChange } from "./hooks/HRM3050_R01CellChange";

// 근무조 그리드의 컬럼 배치·너비를 브라우저에 보관할 때 사용하는 키이다.
const COLUMN_STATE_KEY = 'HRM3050_R01-grid-column-state';
// 클릭·키보드로 편집을 시작할 수 있는 근무조 컬럼 목록. 렌더링마다 바뀌지 않으므로 컴포넌트 밖 상수로 둔다.
const EDITABLE_COLUMNS = ['shiftCd', 'shiftNm', 'startTime', 'endTime', 'remark'];

/** Container에서 전달받는 값의 모양이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM3050_R01HeaderDto> | null>; // 근무조 그리드 인스턴스를 가리키는 ref. 편집 확정·스크롤 이동에 사용한다.
    defaultHRM3050_R01HeaderRow: HRM3050_R01HeaderDto; // 신규 행과 빈 행을 만들 때 쓰는 기본값
    handleHeaderRowClick: (row: HRM3050_R01HeaderDto) => void; // 행 클릭 시 선택 근무조를 바꾸는 함수. 변경이 있으면 확인창을 먼저 띄운다.
}

/**
 * 근무조(정시 출근·퇴근) 목록을 편집 그리드로 렌더링한다.
 * 한 번에 한 근무조만 선택하며, 선택한 행만 편집할 수 있다. 저장 단위가 선택 근무조 1건이기 때문이다.
 * 근무조는 삭제하지 않으므로 행 번호 칸에 +/- 버튼을 두지 않는다. 실행 샘플과 같이 하단 합계 행도 두지 않는다.
 * @param gridRef Content와 공유하여 편집 확정 및 행 이동에 사용한다.
 * @param defaultHRM3050_R01HeaderRow 신규 행과 그리드 기본 행의 초기값이다.
 * @param handleHeaderRowClick 행 클릭 시 호출할 선택 전환 함수이다.
 * @returns 선택 표시, 셀 편집 이벤트가 연결된 근무조 그리드 JSX
 */
export const Table_HRM3050_R01 = (props: Props) => {
    // 구조 분해: props 객체에서 필요한 값을 각각의 변수로 꺼낸다.
    const { gridRef, defaultHRM3050_R01HeaderRow, handleHeaderRowClick } = props;

    // 화면 Store에서 근무조 목록, 원본 목록, 선택 근무조, 컬럼 상태, 스크롤 위치와 변경 함수를 가져온다.
    const {
        HRM3050_R01List, setHRM3050_R01List, updateHRM3050_R01List, originalHRM3050_R01List,
        selectedHRM3050_R01, initSelectedHRM3050_R01,
        columnState, setColumnState, scrollTop, setScrollTop,
    } = useHRM3050_R01Store();

    // true이면 행을 드래그하는 중이다. 드래그 중에는 정렬 후 처리를 건너뛴다.
    const isRowDraggingRef = useRef(false);
    // 선택 근무조의 valueIndex 목록. 공통 행·셀 CSS 규칙이 목록 형태를 받으므로 한 건짜리 배열로 만든다.
    // 의존성: 선택 근무조가 바뀔 때만 다시 만든다.
    const selectedIndexList = useMemo(
        // 삼항 연산자: 선택 근무조가 있으면 그 valueIndex 한 건, 없으면 빈 배열이다.
        () => selectedHRM3050_R01 ? [selectedHRM3050_R01.valueIndex] : [],
        [selectedHRM3050_R01],
    );
    /** 행이 선택 근무조이면 true를 반환한다. 편집 가능 여부 판단에 쓴다. */
    const isSelectedRow = useCallback(
        // ?.: 행 데이터가 없으면 undefined가 되어 false를 반환한다.
        (row?: HRM3050_R01HeaderDto) => !!row && row.valueIndex === selectedHRM3050_R01?.valueIndex,
        [selectedHRM3050_R01],
    );

    // 그리드 기본 설정: 행 ID 생성, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록, 행 번호 칸 렌더러, 스위치 핸들러를 받는다.
    const {
        getRowId, createRowClassRules, createCellClassRules, paddedGridData,
        renderIndexCell, handleSwitchButtonHandler,
    } = useGridSettingContent<HRM3050_R01HeaderDto>({
        gridRef,
        // 각 행을 구분하는 기준 필드
        key: 'valueIndex',
        gridData: HRM3050_R01List,
        defaultGridData: defaultHRM3050_R01HeaderRow,
        selectedRow: selectedHRM3050_R01,
        originalGridData: originalHRM3050_R01List,
        updateRow: updateHRM3050_R01List,
    });

    // 신규·기존 행별 비활성 셀과 포인터·정렬 스타일을 정의한다. 처음 한 번만 만든다.
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        // 모든 행에서 비활성(회색)으로 표시할 컬럼
        disabledCells: [],
        // 신규 행에서만 비활성으로 표시할 컬럼
        insertedDisabledCells: [],
        // 기존 행에서만 비활성으로 표시할 컬럼. 저장된 근무조코드는 다른 화면이 참조하는 기본키라 바꿀 수 없다.
        commonDisabledCells: ['shiftCd'],
        // 모든 행에서 손가락 커서를 표시할 컬럼
        pointerCells: ['shiftNm', 'startTime', 'endTime', 'useYn', 'remark'],
        // 신규 행에서만 손가락 커서를 표시할 컬럼
        insertedPointerCells: ['shiftCd'],
        // 기존 행에서만 손가락 커서를 표시할 컬럼
        commonPointerCells: [],
        // 왼쪽 정렬할 컬럼
        alignLeftCells: ['shiftNm', 'remark'],
    }), []);

    // 위 설정을 신규 행용·기존 행용 목록으로 합친 결과를 구조 분해로 꺼낸다.
    const {
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙(선택·비활성·포인터·정렬)을 더한 객체. 근무조는 삭제 표시가 없다.
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM3050_R01HeaderDto>({
            idKey: 'valueIndex',
            selectedList: selectedIndexList,
            deletedList: [],
            disabledInsertedColumns, disabledColumns,
            pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }),
    }), [createCellClassRules, selectedIndexList,
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

    /** 근무조 그리드 컬럼 정의이다. 모든 편집 컬럼은 선택 근무조 행에서만 편집할 수 있다. */
    const baseColumnDefs = useMemo<ColDef<HRM3050_R01HeaderDto>[]>(() => [
        // 펼치기(...): 행 번호 컬럼 설정 배열의 항목을 이 배열 안에 그대로 풀어 넣는다.
        ...getDefaultRowIndexColumnConfig(
            // 행 번호 칸 렌더러: 번호 또는 I·U 표시를 그린다. 근무조는 삭제·행 추가 버튼을 두지 않으므로 빈 함수를 넘긴다.
            (params: HRM3050_R01HeaderDto) => renderIndexCell(
                params,
                // 행 상태 판단: 공통 함수가 선택·신규·수정 여부를 계산해 반환한다.
                (innerParams: HRM3050_R01HeaderDto) => getRowStyleFlags(
                    innerParams,
                    {
                        deletedList: [],
                        // 공통 함수는 문자열 식별값 목록을 받으므로 선택 근무조 valueIndex를 문자열로 바꿔 넘긴다.
                        selectedList: selectedIndexList.map(String),
                        dataList: HRM3050_R01List,
                    }),
                () => {},
                () => {},
            ),
        ),
        {
            headerName: headerNames_HRM3050_R01.shiftCd, colId: 'shiftCd', field: 'shiftCd', width: 90,
            // 선택한 신규 행에서만 근무조코드를 입력한다. 저장된 코드는 기본키라 바꿀 수 없다.
            editable: params => isSelectedRow(params.data) && params.data?.inserted === true,
            // 코드는 DB 컬럼 길이 20자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 20 },
            // ??: 값이 null·undefined이면 빈 문자열을 쓴다. 앞뒤 공백을 지우고 대문자로 바꿔 저장한다.
            valueParser: params => String(params.newValue ?? '').trim().toUpperCase(),
        },
        {
            headerName: headerNames_HRM3050_R01.shiftNm, colId: 'shiftNm', field: 'shiftNm', width: 150,
            editable: params => isSelectedRow(params.data),
            // 근무조명은 DB 컬럼 길이 50자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: headerNames_HRM3050_R01.startTime, colId: 'startTime', field: 'startTime', width: 110,
            editable: params => isSelectedRow(params.data),
            // HH:MM 5자까지 입력받고, 2130처럼 콜론 없이 입력해도 21:30으로 정리한다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 5 },
            valueParser: params => formatHRM3050_R01Time(params.newValue),
        },
        {
            headerName: headerNames_HRM3050_R01.endTime, colId: 'endTime', field: 'endTime', width: 110,
            editable: params => isSelectedRow(params.data),
            // 퇴근이 출근보다 작으면 자정을 넘기는 야간조로 계산한다. 입력 정리는 정시 출근과 같다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 5 },
            valueParser: params => formatHRM3050_R01Time(params.newValue),
        },
        {
            headerName: headerNames_HRM3050_R01.useYn, colId: 'useYn', field: 'useYn', width: 80,
            // 사용 여부 스위치. 선택 근무조 행에서만 조작할 수 있다. 근무조를 그만 쓸 때는 삭제 대신 N으로 바꾼다.
            ...getSwitchButtonColumnOptions<HRM3050_R01HeaderDto>('useYn', handleSwitchButtonHandler,
                headerNames_HRM3050_R01, aggridSwitchButtonWrapperStyle, params => isSelectedRow(params.data)),
        },
        {
            headerName: headerNames_HRM3050_R01.remark, colId: 'remark', field: 'remark', width: 320,
            editable: params => isSelectedRow(params.data),
            // 기본 텍스트 편집기를 쓰고 최대 1000자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 1000 },
        },
    ], [renderIndexCell, selectedIndexList, HRM3050_R01List, isSelectedRow, handleSwitchButtonHandler]);

    // 기본 컬럼 순서를 생성하고 저장된 컬럼 상태가 있을 때만 그 상태를 적용한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    // 삼항 연산자: columnState가 없으면 기본 정의를, 있으면 사용자가 저장한 배치를 반영한 정의를 반환한다.
    const columnDefs = useMemo(() => columnState === null ? baseColumnDefs : GridCustomColumnDefs<HRM3050_R01HeaderDto>({
        baseColumnDefs, columnState, headerNames: headerNames_HRM3050_R01,
        fallbackHeaderClass: aggridHeaderClass,
    }), [baseColumnDefs, columnState]);

    // 셀 값이 바뀌었을 때 근무조 Store 목록에 반영하는 함수를 가져온다.
    const { handleCellChange } = useHRM3050_R01CellChange<HRM3050_R01HeaderDto>({ updateRow: updateHRM3050_R01List });

    /**
     * 행 클릭으로 선택 근무조를 바꾼다. 하단 고정 행·빈 행은 무시한다.
     * @param event 클릭한 행 정보
     * @returns 반환값은 없다.
     */
    const handleRowClick = useCallback((event: RowClickedEvent<HRM3050_R01HeaderDto>) => {
        // 실제 데이터가 없는 행은 선택 대상이 아니다.
        if (event.node.rowPinned || !event.data) return;
        handleHeaderRowClick(event.data);
    }, [handleHeaderRowClick]);

    // 방향키 이동과 편집 가능 컬럼 내 키보드 탐색을 공통 이벤트 훅에 연결한다.
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM3050_R01List, editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });

    // 셀 클릭 동작 목록: 선택 근무조 행의 편집 컬럼을 클릭하면 셀 편집을 시작한다. 코드 칸은 신규 행에서만 허용한다.
    const cellClickActions = useMemo<CustomCellClickAction<HRM3050_R01HeaderDto>[]>(() => [{
        // 다른 행을 누른 경우에는 편집하지 않고, 행 클릭이 선택 전환을 처리한다.
        validate: (field, data) => EDITABLE_COLUMNS.includes(field) && isSelectedRow(data) &&
            (field !== 'shiftCd' || data.inserted),
        // 유효한 클릭의 셀 편집을 시작한다. 반환값은 없다.
        // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], [isSelectedRow]);

    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(HRM3050_R01List, 'valueIndex', cellClickActions);
    /**
     * 헤더 정렬 후 원본·현재 행 인덱스를 기준으로 표시 순서를 맞춘다.
     * @param params 정렬된 행 정보
     * @returns 반환값은 없다.
     */
    const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<HRM3050_R01HeaderDto>) => {
        if (isRowDraggingRef.current) return;
        postSortHandler(params, HRM3050_R01List, originalHRM3050_R01List, setHRM3050_R01List);
    }, [HRM3050_R01List, originalHRM3050_R01List, setHRM3050_R01List]);

    // 선택·편집 이벤트와 컬럼 상태를 연결한 JSX를 반환한다.
    return (
        // 우클릭 메뉴, 셀 계산, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트. 근무조는 다중 선택이 없어 전체 선택을 넘기지 않는다.
        <GridContextWrapper<HRM3050_R01HeaderDto>
            enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedHRM3050_R01}
            defaultRowData={defaultHRM3050_R01HeaderRow}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            {/* 공통 파란 막대 제목으로 근무조 영역을 표시한다. */}
            <SmallTitleWithSubSpan title="근무조 (정시 출근·퇴근)" />
            {/* 근무조 편집 그리드: 행 데이터, 컬럼 정의, 선택 행 CSS 규칙, 클릭·키보드·값 변경·정렬 이벤트를 연결한다. */}
            <Grid_Excel<HRM3050_R01HeaderDto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM3050_R01HeaderDto>({
                    idKey: 'valueIndex',
                    selectedList: selectedIndexList,
                    deletedList: [],
                })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                onPostSortRows={handlePostSortWithDrag}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
};
